import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth";
import { type Order, ShipmentStatus } from "@/api.types.v2";

interface PaginationMetadata {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CustomerOrdersResponse {
  data: Order[];
  metadata: PaginationMetadata;
}

/**
 * Fetch customer orders using v2 API
 * Returns orders with shipments (not parcels)
 */
export function useCustomerOrdersV2({
  page = 1,
  limit = 50,
}: {
  page?: number;
  limit?: number;
}) {
  const { token, customer } = useSession();

  return useQuery<CustomerOrdersResponse>({
    queryKey: ["customerOrdersV2", customer?.id, page, limit],
    queryFn: async () => {
      const response = await axios.get<CustomerOrdersResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/customers/me/orders`,
        {
          params: { page, limit },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    enabled: !!token && !!customer?.id,
  });
}

// Original payment prompts require a financial obligation as well as a
// Shipment awaiting preparation. Return transportation and invoices own theirs.
export function isOrderAwaitingPayment(order: Order): boolean {
  return (
    order.shipments?.[0]?.status === ShipmentStatus.PENDING_PAYMENT &&
    !order.shipments.some((shipment) => shipment.cancellationRequestedAt) &&
    ["PENDING", "FAILED", "PARTIAL"].includes(order.paymentStatus ?? "") &&
    (order.balanceCents ?? order.totalCents ?? 0) > 0
  );
}

// Status colors for shipment statuses
export const shipmentStatusColors: Record<ShipmentStatus, string> = {
  [ShipmentStatus.PENDING_PAYMENT]: "bg-yellow-500",
  [ShipmentStatus.LABEL_READY]: "bg-blue-500",
  [ShipmentStatus.DROPPED_AT_CARRIER]: "bg-indigo-500",
  [ShipmentStatus.PICKED_UP]: "bg-indigo-600",
  [ShipmentStatus.IN_TRANSIT]: "bg-cyan-500",
  [ShipmentStatus.AT_FACILITY]: "bg-purple-500",
  [ShipmentStatus.OUT_FOR_DELIVERY]: "bg-yellow-600",
  [ShipmentStatus.DELIVERED]: "bg-green-600",
  [ShipmentStatus.AVAILABLE_FOR_PICKUP]: "bg-green-500",
  [ShipmentStatus.DELIVERY_ATTEMPTED]: "bg-orange-500",
  [ShipmentStatus.EXCEPTION]: "bg-red-500",
  [ShipmentStatus.ON_HOLD]: "bg-gray-500",
  [ShipmentStatus.RETURNED]: "bg-orange-600",
  [ShipmentStatus.CANCELLED]: "bg-red-700",
};

// Helper to get primary shipment status for display
export function getOrderStatus(order: Order): ShipmentStatus | null {
  if (!order.shipments?.length) return null;
  // Use first shipment status as primary
  return order.shipments[0].status;
}

// Helper to format recipient info from shipment
export function getRecipientFromOrder(order: Order): {
  name: string;
  fullAddress: string;
  phone?: string;
  email?: string;
} | null {
  if (!order.shipments?.length) return null;
  const shipment = order.shipments[0];

  // Pattern B (task #258): role-tagged addresses[] is the source of truth.
  // Note: API returns contactName/contactPhone/contactEmail, but types say name/phone/email.
  const toAddress = shipment.addresses?.find((a) => a.type === "TO");
  if (!toAddress) return null;

  const addr = toAddress.address;
  const parts = [
    addr?.line1,
    addr?.line2,
    addr?.city,
    addr?.province,
    addr?.postalCode,
  ].filter(Boolean);
  return {
    name: toAddress.contactName ?? toAddress.name ?? "",
    fullAddress: parts.join(", "),
    phone: toAddress.contactPhone ?? toAddress.phone,
    email: toAddress.contactEmail ?? toAddress.email,
  };
}

// Helper to check if order is paid
export function isOrderPaid(order: Order): boolean {
  if (!order.shipments?.length) return false;
  return order.shipments[0].status !== ShipmentStatus.PENDING_PAYMENT;
}
